import { push } from "connected-react-router"
import { useSnackbar } from "notistack"
import { useRefresh, useDelete, CRUD_DELETE, crudGetList, ReduxState, useCreate, useUpdate } from "ra-core"
import { createContext, FC, useCallback, useContext, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { RecPatch, SupportedResources } from "~/dataProvider/tools"
import { MutationCallbacks, SaveActionSlim } from "~/dataProvider/types"
import { CreateNetworkConnectionInput, CreateNetworkGatewayInput, NetworkGatewayResponseDto, NetworkGatewayStatus, UpdateNetworkGatewayInput } from "~/gen/api/deploy"
import chainOptions from "~/utils/chainOptions"
import { genUrlWithHashid } from "~/utils/hashid"
import { pages, sorts } from "~/utils/paramsWithGetMainList"
import retrieveAxiosErr from "~/utils/retrieveAxiosErr"


interface NetworkConfigurationControllerProps {
  deleteNetworkConn: (id:number, options?: MutationCallbacks) => void;
  deleting: boolean;
  vpnGatewayRec?: NetworkGatewayResponseDto;
  deleteGateway: (options:MutationCallbacks) => void;
  gatewayDeleting: boolean;
  createGateway: SaveActionSlim<CreateNetworkGatewayInput>;
  createConnection: SaveActionSlim<CreateNetworkConnectionInput>;
  changeGatewayTier: SaveActionSlim<string>;
}

const NetworkConfigurationContext = createContext<NetworkConfigurationControllerProps>({
  deleteNetworkConn: () => {},
  deleting: false,
  vpnGatewayRec: undefined,
  deleteGateway: () => {},
  gatewayDeleting: false,
  createGateway: () => {},
  createConnection: () => {},
  changeGatewayTier: () => {},
})


export const NetworkConfigurationContextProvider: FC<{ value: NetworkConfigurationControllerProps }> = ({ children, value }) => {
  return (
    <NetworkConfigurationContext.Provider value={value}>
      {children}
    </NetworkConfigurationContext.Provider>
  )
}

const gatewayResource: SupportedResources = 'settings/deployment/network-configuration'
const connectionResource: SupportedResources = 'network-connections'

export const useNetworkConfigurationController = (): NetworkConfigurationControllerProps => {
  const { enqueueSnackbar } = useSnackbar()
  const refresh = useRefresh()
  const dispatch = useDispatch()

  const [deleteOne, { loading }] = useDelete('network-connections', undefined, null, {
    action: CRUD_DELETE,
  })

  const handleDelete = useCallback((id:number, options?: MutationCallbacks) => {
    deleteOne('network-connections', id, undefined, chainOptions(options, {
      onSuccess: () => { 
        enqueueSnackbar('Delete Network Connection succeed', { variant: 'success' })
        refresh()
      },
      onFailure: (err) => {
        const errMsg = retrieveAxiosErr(err)
        enqueueSnackbar(`Delete failed: ${errMsg}`, { variant: 'error' })
      },
    }))
  }, [deleteOne])

  useEffect(() => {
    dispatch(crudGetList(gatewayResource, pages(), sorts(), {}))
  }, [])

  const vpnGatewayRec = useSelector<any, NetworkGatewayResponseDto & RecPatch | undefined>((store:ReduxState) => {
    const resContents = store.admin.resources[gatewayResource]
    const id = Math.max.apply(Math, 
      (resContents.list.ids || []) as number[])
    return resContents.data[id]
  })


  const [deleteOneGateway, { loading: gatewayDeleting }] = useDelete(gatewayResource, undefined, null, {
    action: CRUD_DELETE,
  })

  const handleGatewayDelete = useCallback((options:MutationCallbacks) => {
    deleteOneGateway(gatewayResource, vpnGatewayRec?.networkGatewayId, undefined, chainOptions(options, {
      onSuccess: () => {
        enqueueSnackbar('Delete VPN Gateway succeed', { variant: 'success' })
        refresh()
      },
      onFailure: (err) => {
        const errMsg = retrieveAxiosErr(err)
        enqueueSnackbar(`Delete failed: ${errMsg}`, { variant: 'error' })
      },
    }))
  }, [deleteOneGateway, vpnGatewayRec])

  useEffect(() => {
    let timer = window.setInterval(() => {
      dispatch(crudGetList(gatewayResource, pages(), sorts(), {}))
    }, 30 * 1000)
    return () => {
      timer && clearInterval(timer)
    }
  }, [])

  const [createGateway] = useCreate<CreateNetworkGatewayInput & RecPatch>(gatewayResource)
  const handleCreateGateway = useCallback<SaveActionSlim<CreateNetworkGatewayInput>>((gatewayInput, options) => {
    createGateway(gatewayResource, gatewayInput, chainOptions(options, {
      onSuccess: () => {
        enqueueSnackbar('VPN Gateway deployment recieved, please wait a few minutes before it\'s ready.', { variant: 'success' })
        refresh()
      },
      onFailure: (err) => {
        const errMsg = retrieveAxiosErr(err)
        enqueueSnackbar(`VPN Gateway deployment failed: ${errMsg}`, { variant: 'error' })
      },
    }))
  }, [createGateway, enqueueSnackbar])

  const [createConnection] = useCreate<CreateNetworkConnectionInput & RecPatch>(connectionResource, undefined, { mutationMode: 'pessimistic' })
  const handleCreateConnection = useCallback<SaveActionSlim<CreateNetworkConnectionInput>>((connInput, callbacks) => {
    createConnection && createConnection(connectionResource, {...connInput, gatewayId: vpnGatewayRec?.networkGatewayId}, chainOptions(callbacks, {
      onSuccess: ({data}: {data:RecPatch}) => {
        dispatch(push(genUrlWithHashid(`/${gatewayResource}/$0/show`, data.id, connInput.name)))
        enqueueSnackbar('Create network connection succeed', { variant: 'success' })
      },
      onFailure: (err) => {
        const errMsg = retrieveAxiosErr(err)
        enqueueSnackbar(`Create network connection failed: ${errMsg}`, { variant: 'error' })
      },
    }))
  }, [createConnection, dispatch, enqueueSnackbar, vpnGatewayRec])

  const [changeGatewayTier] = useUpdate<UpdateNetworkGatewayInput & RecPatch>(gatewayResource)
  const handleChangeGatewayTier: SaveActionSlim<string> = (performanceTier, options) => {
    changeGatewayTier(gatewayResource, vpnGatewayRec?.id, { 
      id: vpnGatewayRec?.id, 
      networkGatewayId: vpnGatewayRec?.networkGatewayId, 
      performanceTier 
    }, undefined, chainOptions(options, {
      onSuccess: () => {
        enqueueSnackbar('Update VPN Gateway recieved, please wait a few minutes before it\'s ready', { variant: 'success' })
        dispatch({
          type: "OD/VPN_GATEWAY_STATUS_CHANGE",
          payload: { data: { ...vpnGatewayRec, performanceTier, status: NetworkGatewayStatus.Updating }},
          requestPayload: { id: vpnGatewayRec?.id},
          meta: {
              resource: gatewayResource,
              fetchResponse: 'GET_ONE',
              fetchStatus: 'RA/FETCH_END',
          },
        })
        refresh()
      },
      onFailure: (err) => {
        const errMsg = retrieveAxiosErr(err)
        enqueueSnackbar(`Update VPN Gateway failed: ${errMsg}`, { variant: 'error' })
      },
    }))
  }

  return {
    deleteNetworkConn: handleDelete,
    deleting: loading,
    vpnGatewayRec,
    deleteGateway: handleGatewayDelete,
    gatewayDeleting,
    createGateway: handleCreateGateway,
    createConnection: handleCreateConnection,
    changeGatewayTier: handleChangeGatewayTier,
  }
}

export const useNetworkConfigurationContext = () => useContext(NetworkConfigurationContext);

